import React, { useEffect } from "react";
import {

  Copyright, Smartphone, MapPin, Star,
  ShieldCheck, Globe, ArrowRight, Home,
 
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import AppleLogo   from "../assets/svg/apple.svg";
import GoogleLogo  from "../assets/svg/google.svg";
import StripeImage from "../assets/app/stripe.png";
import logoImg     from "../assets/app/logo.png";
import { fetchSocialLinks } from "../redux/socialSlice";
import { useNavigate } from "react-router-dom";
import { Facebook, Instagram, Twitter, YouTube } from "@mui/icons-material";


/* ─────────────────────────────────────────────────────────
   THEME — AE CUTE (matches UIHome palette)
───────────────────────────────────────────────────────── */
const C = {
  primary:  "#e84020",
  dark:     "#0a0a0a",
  white:    "#ffffff",
  offwhite: "#f7f6f4",
  muted:    "#6b6b6b",
  border:   "#e8e8e8",
  gold:     "#FFC501",
  green:    "#0F3D2E",
  cream:    "#F6EFDD",
  sans:     "'DM Sans', sans-serif",
  display:  "'Sora', sans-serif",
};

/* ae brand gradient */
const AE_G = "linear-gradient(90deg,#0a0a0a,#bf9b30,#d46b10,#e84020,#bf9b30,#0a0a0a)";

/* 3D layered text-shadow, matching the IntroShowcase treatment */
const TEXT_3D = "0 1px 0 #fff, 0 2px 0 #e8e0d0, 0 3px 0 #dcd0b8, 0 4px 6px rgba(0,0,0,0.15)";
const TEXT_3D_LIGHT = "0 1px 0 rgba(255,255,255,0.5), 0 2px 4px rgba(0,0,0,0.12)";

const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap');
  *,*::before,*::after{box-sizing:border-box;}

  /* ── animated gradient keyframes ── */
  @keyframes aeSpin{
    0%  {background-position:0% 50%;}
    50% {background-position:100% 50%;}
    100%{background-position:0% 50%;}
  }
  @keyframes shimmer{
    0%  {background-position:-200% center;}
    100%{background-position: 200% center;}
  }

  /* ── nav links ── */
  .af-link{
    color:#0a0a0a;text-decoration:none;font-size:13px;
    letter-spacing:0.01em;font-family:'DM Sans',sans-serif;
    font-weight:600;transition:color 0.2s,transform 0.15s;cursor:pointer;display:block;
  }
  .af-link:hover{color:#e84020!important;transform:translateX(2px);}

  /* ── social icons ── */
  .af-social{
    width:42px;height:42px;border:2.5px solid #0a0a0a;border-radius:12px;
    display:flex;align-items:center;justify-content:center;
    color:#0a0a0a;text-decoration:none;flex-shrink:0;background:#fff;
    transition:background 0.2s,color 0.2s,transform 0.18s;
  }
  .af-social:hover{background:#0a0a0a!important;color:#fff!important;transform:translateY(-3px);}

  /* ── app store buttons ── */
  .af-store-btn{
    display:flex;align-items:center;gap:10px;
    background:#fff;border:2.5px solid #0a0a0a;border-radius:14px;
    padding:10px 18px;cursor:pointer;text-decoration:none;
    transition:transform 0.18s,background 0.2s;flex-shrink:0;
  }
  .af-store-btn:hover{transform:translateY(-2px);background:#f7f6f4!important;}

  /* ── trustpilot review btn ── */
  .af-tp-review{
    display:inline-flex;align-items:center;gap:6px;
    padding:10px 18px;background:#00b67a;border-radius:100px;
    border:2.5px solid #0a0a0a;color:#fff;
    font-size:11px;font-weight:800;letter-spacing:0.1em;
    text-decoration:none;font-family:'Sora',sans-serif;
    cursor:pointer;transition:transform 0.18s;
  }
  .af-tp-review:hover{transform:translateY(-2px);}

  /* ── animated ae-gradient pill ── */
  .af-btn-host{
    position:relative;display:inline-flex;align-items:center;gap:9px;
    background:#e84020;color:#fff;border:3px solid #0a0a0a;border-radius:100px;
    padding:14px 28px;
    font-family:'Sora',sans-serif;font-size:13px;font-weight:800;
    cursor:pointer;overflow:hidden;
    transition:transform 0.18s,background 0.2s;
    white-space:nowrap;text-decoration:none;letter-spacing:0.02em;
  }
  .af-btn-host:hover{transform:translateY(-3px);background:#c73318;}

  /* ── co-host ghost pill ── */
  .af-btn-cohost{
    display:inline-flex;align-items:center;gap:9px;
    background:transparent;color:#0a0a0a;
    border:3px solid #0a0a0a;border-radius:100px;
    padding:13px 26px;
    font-family:'Sora',sans-serif;font-size:13px;font-weight:800;
    cursor:pointer;transition:background 0.2s,color 0.2s,transform 0.18s;
    white-space:nowrap;text-decoration:none;letter-spacing:0.02em;
  }
  .af-btn-cohost:hover{background:#0a0a0a;color:#fff;transform:translateY(-2px);}

  /* ── shimmer on brand text ── */
  .af-shimmer{
    background:linear-gradient(90deg,#bf9b30 0%,#0a0a0a 30%,#d46b10 55%,#e84020 75%,#bf9b30 100%);
    background-size:200% auto;
    -webkit-background-clip:text;-webkit-text-fill-color:transparent;
    background-clip:text;
    animation:shimmer 4s linear infinite;
  }

  /* ════════ DESKTOP layout ════════ */
  .af-header-inner{
    max-width:1100px;margin:0 auto;
    padding:52px 48px 44px;
    display:flex;
    flex-direction:row;
    align-items:center;
    justify-content:space-between;
    gap:40px;
    flex-wrap:wrap;
  }
  .af-wrap{
    max-width:1100px;margin:0 auto;
    padding:48px 48px 28px;
    display:flex;flex-direction:column;gap:40px;
  }
  .af-link-grid{
    display:grid;
    grid-template-columns:repeat(4,1fr);
    gap:32px 24px;
  }
  .af-brand-row{display:flex;align-items:flex-start;justify-content:space-between;gap:28px;flex-wrap:wrap;}
  .af-app-row{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:20px;}
  .af-app-btns{display:flex;gap:10px;}
  .af-tp-inner{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:20px;}
  .af-social-row{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:20px;}
  .af-bottom{display:flex;flex-wrap:wrap;align-items:center;gap:12px;}
  .af-cta-row{display:flex;gap:14px;flex-wrap:wrap;align-items:center;}

  /* ════════ MOBILE ════════ */
  @media(max-width:640px){

    .af-header-inner{
      flex-direction:row!important;
      align-items:flex-start!important;
      text-align:left!important;
      padding:28px 16px 24px!important;
      gap:16px!important;
      flex-wrap:nowrap!important;
    }
    .af-header-left{
      width:100%!important;
      align-items:flex-start!important;
      text-align:left!important;
    }
    .af-logo-block{
      justify-content:flex-start!important;
      width:100%!important;
    }
    .af-cta-row{
      flex-direction:row!important;
      width:100%!important;
      gap:8px!important;
      flex-wrap:wrap!important;
    }
    .af-btn-host,.af-btn-cohost{
      flex:1 1 auto!important;
      justify-content:center!important;
      font-size:11px!important;
      padding:11px 12px!important;
    }
    .af-trust-row{
      justify-content:flex-start!important;
      gap:10px!important;
    }
    .af-stripe-img{ display:none!important; }

    .af-wrap{ padding:28px 16px 20px!important; gap:28px!important; }

    .af-brand-row{
      flex-direction:row!important;
      flex-wrap:wrap!important;
      gap:12px!important;
    }

    .af-link-grid{
      grid-template-columns:1fr 1fr!important;
      gap:24px 20px!important;
    }

    .af-app-row{
      flex-direction:column!important;
      align-items:flex-start!important;
      gap:16px!important;
    }
    .af-app-btns{
      flex-direction:row!important;
      width:100%!important;
      gap:8px!important;
    }
    .af-store-btn{
      flex:1 1 auto!important;
      justify-content:center!important;
    }

    .af-tp-inner{
      flex-direction:row!important;
      align-items:center!important;
      justify-content:space-between!important;
      gap:12px!important;
      flex-wrap:wrap!important;
    }

    .af-social-row{
      flex-direction:row!important;
      align-items:center!important;
      justify-content:space-between!important;
      gap:12px!important;
      flex-wrap:wrap!important;
    }

    .af-bottom{
      gap:8px!important;
      flex-direction:row!important;
      align-items:center!important;
      flex-wrap:wrap!important;
    }
  }
`;

const currentYear = new Date().getFullYear();

/* ─────────────────────────────────────────────────────────
   TRUSTPILOT STARS
───────────────────────────────────────────────────────── */
const TPStars: React.FC<{ rating?: number }> = ({ rating = 4.7 }) => (
  <div style={{ display:"flex", alignItems:"center", gap:3 }}>
    {[1,2,3,4,5].map(n => (
      <div key={n} style={{
        width:20, height:20, borderRadius:5,
        background: n <= Math.round(rating) ? "#00b67a" : "#e8e8e8",
        display:"flex", alignItems:"center", justifyContent:"center",
      }}>
        <Star size={11} color="#fff" fill="#fff" strokeWidth={0}/>
      </div>
    ))}
  </div>
);

/* ─────────────────────────────────────────────────────────
   AE LOGO with animated gradient ring (matches AeRing in UIHome)
───────────────────────────────────────────────────────── */
const AeLogo: React.FC<{ size?: number }> = ({ size = 56 }) => (
  <div style={{ position:"relative", display:"inline-flex", flexShrink:0 }}>
    <div style={{
      position:"absolute", inset:-3,
      borderRadius: size * 0.24 + 6,
      background: AE_G,
      backgroundSize:"300% 100%",
      animation:"aeSpin 3s linear infinite",
      zIndex:0,
    }}/>
    <div style={{
      position:"relative", zIndex:1,
      background:"#fff",
      borderRadius: size * 0.24 + 3,
      padding:3,
      display:"inline-flex",
    }}>
      <img
        src={logoImg}
        alt="Areluxe"
        style={{
          width:size, height:size,
          borderRadius: size * 0.22,
          objectFit:"cover",
          display:"block",
        }}
      />
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   MAIN
════════════════════════════════════════════════════════════ */
const Footer: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();

  const user      = useSelector((state: RootState) => state.auth.user);
  const { social } = useSelector((state: RootState) => state.social);

  useEffect(() => { dispatch(fetchSocialLinks()); }, [dispatch]);

  const buildSocialLink = (platform: string, handle?: string) => {
    if (!handle) return `https://${platform}.com`;
    if (handle.startsWith("http")) return handle;
    if (platform === "youtube") return `https://www.youtube.com/@${handle}`;
    return `https://${platform}.com/${handle}`;
  };

 const socialIcons = [
    { icon:<Facebook  sx={{ fontSize: 17 }}/>, href:buildSocialLink("facebook",  social?.facebook),  label:"Facebook"  },
    { icon:<Instagram sx={{ fontSize: 17 }}/>, href:buildSocialLink("instagram", social?.instagram), label:"Instagram" },
    { icon:<Twitter   sx={{ fontSize: 17 }}/>, href:buildSocialLink("twitter",   social?.twitter),   label:"Twitter"   },
    { icon:<YouTube   sx={{ fontSize: 17 }}/>, href:buildSocialLink("youtube",   social?.youtube),   label:"YouTube"   },
  ];

  const NavLink: React.FC<{ label:string; to:string }> = ({ label, to }) =>
    to.startsWith("/") || to.startsWith("http") === false ? (
      <span className="af-link" onClick={() => navigate(to)}>{label}</span>
    ) : (
      <a className="af-link" href={to} target="_blank" rel="noopener noreferrer">{label}</a>
    );

  const handleBecomeHost = () => {
    if (user?.userId) navigate(`/onboarding/${user.userId}`);
    else navigate("/hosting");
  };
  const handleCoHost = () => {
    if (user?.userId) navigate(`/partneronboarding/${user.userId}`);
    else navigate("/authswitch");
  };

  return (
    <footer style={{ background:C.white, fontFamily:C.sans, color:C.dark }}>
      <style>{FONT_CSS}</style>

      {/* ══════════════════════════════════════════════════════
          HEADER SECTION
      ══════════════════════════════════════════════════════ */}
      <section style={{
        background:C.offwhite,
        borderBottom:`3px solid ${C.dark}`,
        position:"relative",
        overflow:"hidden",
      }}>



      </section>

      {/* ══════════════════════════════════════════════════════
          BODY
      ══════════════════════════════════════════════════════ */}
      <div className="af-wrap">

        {/* ════════════════════════════
            BRAND ROW
        ════════════════════════════ */}
        <div className="af-brand-row">
          <div style={{ width: "100%" }}>
            {/* Logo + wordmark row */}
            <div className="af-logo-block" style={{ display:"flex", alignItems:"center", gap:16, width:"100%" }}>
              <AeLogo size={56} />
              <div>
                <div className="af-shimmer" style={{
                  fontFamily:"'Sora',sans-serif",
                  fontSize:30, fontWeight:900,
                  letterSpacing:"-0.02em", lineHeight:1,
                  textShadow: TEXT_3D,
                }}>
                 HEARTLAS
                </div>
                {/*
                <p style={{
                  fontSize:8, color:C.muted,
                  letterSpacing:"0.26em", marginTop:7,
                  fontWeight:300, fontFamily:C.sans,
                }}>
                  REAL ESTATE · GLOBAL REACH
                </p>
                */}
              </div>
            </div>
          </div>

          {/* radial glow */}
          <div style={{
            position:"absolute", top:-80, left:"50%", transform:"translateX(-50%)",
            width:600, height:400, pointerEvents:"none",
            background:"radial-gradient(ellipse, rgba(232,64,32,0.08) 0%, transparent 68%)",
          }}/>

          <div className="af-header-inner">

            {/* ── LEFT: logo + wordmark + headline + CTAs ── */}
            <div className="af-header-left" style={{ display:"flex", flexDirection:"column", gap:22, flex:1, minWidth:0 }}>

              {/* Headline
               <div>
                <h2 style={{
                  fontFamily:C.serif,
                  fontSize:30, fontWeight:500, color:C.white,
                  lineHeight:1.3, letterSpacing:"-0.01em",
                  margin:"0 0 10px",
                }}>
                  Your property.<br/>Your income. Your terms.
                </h2>
                <p style={{
                  fontSize:12, color:C.muted,
                  lineHeight:1.85, letterSpacing:"0.04em",
                  maxWidth:440,
                }}>
                  Join thousands of hosts already earning across 100+ countries.
                  List in minutes. Get paid instantly.
                </p>
              </div>


              */}


              {/* CTAs

              <div className="af-cta-row">
                <button className="af-btn-host" onClick={handleBecomeHost}>
                  <Home size={14}/> Become a Host <ArrowRight size={13}/>
                </button>
                <button className="af-btn-cohost" onClick={handleCoHost}>
                  Find a Co-Host <ArrowRight size={13}/>
                </button>
              </div>




               */}

              {/* Trust badges — removed per redesign
              <div className="af-trust-row" style={{ display:"flex", alignItems:"center", gap:20, flexWrap:"wrap" }}>
                {["Instant Payouts","Verified Guests","EU Regulated","24/7 Support"].map(label => (
                  <div key={label} style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <span style={{ color:"#e84020", fontSize:10, fontWeight:700 }}>✓</span>
                    <span style={{ fontSize:9, color:C.muted, letterSpacing:"0.1em" }}>{label}</span>
                  </div>
                ))}
              </div>
              */}
            </div>
          </div>

        </div>

        {/* RULE */}
        <div style={{ height:3, background:C.dark, borderRadius:2 }}/>

        {/* ════════════════════════════
            LINK COLUMNS
        ════════════════════════════ */}
        <div className="af-link-grid">

          <div style={{ display:"flex", flexDirection:"column", gap:12, background:"#fff", border:`3px solid ${C.dark}`, borderRadius:20, padding:"22px 22px" }}>
            <p style={S.colHead}>COMPANY</p>
            {[
              ["About Us",    "https://areluxe.com/pages/about-us"],
              ["List Your Property",    "https://areluxe.com/hosting"],
              ["Book Your Stay",    "https://areluxe.com/vacationrentals"],
              //["Careers",     "https://areluxe.com/pages/careers"],
              ["Newsroom",    "https://areluxe.com/pages/news"],
              ///["Investors",   "https://areluxe.com/pages/investors"],
              /// ["Gift Cards",  "https://areluxe.com/pages/gift-cards"],
              //["Blog",        "/News"],
            ].map(([l,h]) => <NavLink key={l} label={l} to={h}/>)}
          </div>


          <div style={{ display:"flex", flexDirection:"column", gap:12, background:"#fff", border:`3px solid ${C.dark}`, borderRadius:20, padding:"22px 22px" }}>
            <p style={S.colHead}>HOSTING</p>
            <span className="af-link" onClick={handleBecomeHost}>Become a Host</span>
            {[
              ["How To Onboard", "/Hosting"],
              // ["Cancellfirebase deploy --only hosting ations",  "/CancellationPolicy"],
            ].map(([l,h]) => <NavLink key={l} label={l} to={h}/>)}
            {/*  <span className="af-link" onClick={handleCoHost}>Find a Co-Host</span>  */}
          </div>


          <div style={{ display:"flex", flexDirection:"column", gap:12, background:"#fff", border:`3px solid ${C.dark}`, borderRadius:20, padding:"22px 22px" }}>
            <p style={S.colHead}>SUPPORT</p>
            {[
              ["Help Center",  "https://areluxe.com/support"],
              ["Contact Us",   "/contact-us"],

              ["Safety Info",  "https://areluxe.com/pages/safety"],
              ["Disability",   "https://areluxe.com/pages/disability-support"],
            ].map(([l,h]) => <NavLink key={l} label={l} to={h}/>)}
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:12, background:"#fff", border:`3px solid ${C.dark}`, borderRadius:20, padding:"22px 22px" }}>
            <p style={S.colHead}>LEGAL</p>
            {[
              ["Terms & Conditions ",     "https://areluxe.com/pages/terms"],
              ["Privacy Policy",       "https://areluxe.com/pages/privacy-settings"],
              ["Cancellation & Refund Policy",       "https://areluxe.com/pages/cancellation-refund-policy"],
              ["Privacy Settings", "https://areluxe.com/pages/privacy-settings"],
              ["Your Privacy Choices", "https://areluxe.com/pages/your-privacy-choices"],
              ["Legal",                "https://areluxe.com/pages/legal"],
              ["Sitemap",              "https://areluxe.com/sitemap.xml"],
            ].map(([l,h]) => <NavLink key={l} label={l} to={h}/>)}
          </div>

        </div>

        {/* RULE */}
        <div style={{ height:3, background:C.dark, borderRadius:2 }}/>

        {/* ════════════════════════════
            APP DOWNLOAD
        ════════════════════════════ */}
        <div className="af-app-row" style={{ background:C.offwhite, border:`3px solid ${C.dark}`, borderRadius:20, padding:"24px 26px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:40, height:40, borderRadius:12, background:"#fff", border:`2.5px solid ${C.dark}`, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <Smartphone size={18} color={C.dark} strokeWidth={2}/>
            </div>
            <div>
              <p style={{ fontSize:14, fontWeight:900, letterSpacing:"-0.01em", color:C.dark, margin:0, fontFamily:C.display, textShadow:TEXT_3D_LIGHT }}>
                DOWNLOAD THE ARELUXE APP
              </p>
              <p style={{ fontSize:11, color:C.muted, marginTop:4, fontWeight:600 }}>
                Available on iOS and Android
              </p>
            </div>
          </div>
          <div className="af-app-btns">
            <a href="https://play.google.com/store/apps/details?id=com.areluxe.android"
              target="_blank" rel="noopener noreferrer"
              className="af-store-btn">
              <img src={GoogleLogo} alt="Google Play" style={{ height:22, objectFit:"contain" }}/>
              <div>
                <div style={{ fontSize:8, color:C.muted, letterSpacing:"0.1em", fontWeight:700 }}>GET IT ON</div>
                <div style={{ fontSize:12, color:C.dark, fontWeight:800, letterSpacing:"0.01em" }}>Google Play</div>
              </div>
            </a>
            <a href="https://apps.apple.com/app/idYOUR_APP_ID"
              target="_blank" rel="noopener noreferrer"
              className="af-store-btn">
              <img src={AppleLogo} alt="App Store"
                style={{ height:22, objectFit:"contain" }}/>
              <div>
                <div style={{ fontSize:8, color:C.muted, letterSpacing:"0.1em", fontWeight:700 }}>DOWNLOAD ON THE</div>
                <div style={{ fontSize:12, color:C.dark, fontWeight:800, letterSpacing:"0.01em" }}>App Store</div>
              </div>
            </a>
          </div>
        </div>

        {/* ════════════════════════════
            TRUSTPILOT
        ════════════════════════════ */}
        <div style={{ border:`3px solid ${C.dark}`, borderRadius:20, background:"#fff", padding:"24px 26px" }}>
          <div className="af-tp-inner">

            {/* Left — rating info */}
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ width:24, height:24, borderRadius:6, background:"#00b67a", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <Star size={13} color="#fff" fill="#fff" strokeWidth={0}/>
                </div>
                <span style={{ fontFamily:C.display, fontSize:17, fontWeight:900, color:C.dark, letterSpacing:"-0.01em", textShadow:TEXT_3D_LIGHT }}>
                  Trustpilot
                </span>
              </div>
              <TPStars />
            </div>

            {/* Right — CTA */}
            <div style={{ display:"flex", flexDirection:"column", gap:10, alignItems:"flex-end" }}>
              <a href="https://www.trustpilot.com/review/areluxe.com"
                target="_blank" rel="noopener noreferrer"
                className="af-tp-review">
                <Star size={12} color="#fff" strokeWidth={1.5}/>
                WRITE US A REVIEW
              </a>
              <span style={{ fontSize:10, color:C.muted, fontWeight:600, letterSpacing:"0.04em" }}>
                Verified by Trustpilot
              </span>
            </div>

          </div>
        </div>

        {/* ── Stripe / secure payments card ── */}
        <div style={{
          border:`3px solid ${C.dark}`, borderRadius:20, background:"#fff",
          padding:"20px 26px", display:"flex", alignItems:"center",
          justifyContent:"space-between", flexWrap:"wrap", gap:16,
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ width:40, height:40, borderRadius:12, background:C.offwhite, border:`2.5px solid ${C.dark}`, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <ShieldCheck size={18} color={C.dark} strokeWidth={2}/>
            </div>
            <div>
              <p style={{ fontSize:13, fontWeight:900, letterSpacing:"-0.01em", color:C.dark, margin:0, fontFamily:C.display, textShadow:TEXT_3D_LIGHT }}>
                SECURE PAYMENTS
              </p>
              <p style={{ fontSize:11, color:C.muted, marginTop:4, fontWeight:600 }}>
                All transactions are encrypted and processed safely
              </p>
            </div>
          </div>
          <img
            src={StripeImage}
            alt="Secure payments powered by Stripe"
            className="af-stripe-img"
            style={{ height:34, objectFit:"contain", opacity:0.85, flexShrink:0 }}
          />
        </div>

        {/* RULE */}
        <div style={{ height:3, background:C.dark, borderRadius:2 }}/>

        {/* ════════════════════════════
            SOCIALS
        ════════════════════════════ */}
        <div className="af-social-row">
          <div>
            <p style={{ fontSize:14, fontWeight:900, letterSpacing:"-0.01em", color:C.dark, margin:0, fontFamily:C.display, textShadow:TEXT_3D_LIGHT }}>
              CONNECT WITH US
            </p>
            <p style={{ fontSize:11, color:C.muted, marginTop:5, fontWeight:600 }}>
              Share your Areluxe experience
            </p>
          </div>
          <div style={{ display:"flex", gap:10 }}>
            {socialIcons.map(({ icon, href, label }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                className="af-social" title={label}>
                {icon}
              </a>
            ))}
          </div>
        </div>

        {/* RULE */}
        <div style={{ height:3, background:C.dark, borderRadius:2 }}/>

        {/* ════════════════════════════
            BOTTOM LEGAL ROW
        ════════════════════════════ */}
        <div className="af-bottom">
          <div style={{ display:"flex", alignItems:"center", gap:5, flexShrink:0 }}>
            <Copyright size={12} color={C.muted} strokeWidth={2}/>
            <span style={{ fontSize:11, color:C.muted, fontWeight:600, letterSpacing:"0.02em" }}>
              2026 – {currentYear} Heartlas, Inc.
            </span>
          </div>
          <span style={{ color:C.border, fontSize:10 }}>·</span>
          {[
            ["About Us",      "https://heartlas.com/pages/about-us"],
            ["Terms",         "https://heartlas.com/pages/terms"],
            ["Privacy",       "https://heartlas.com/pages/privacy-settings"],
            ["Legal",         "https://heartlas.com/pages/legal"],
            ["News",          "/News"],
            ["Sitemap",       "https://heartlas.com/sitemap.xml"],
            ["Become a Host", "/authswitch"],
            ["Contact Us",    "/contact-us"],
          ].map(([l,h]) => <NavLink key={l} label={l} to={h}/>)}
        </div>

      </div>{/* end af-wrap */}

    </footer>
  );
};

export default Footer;

/* ─────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────── */
const S: { [key: string]: React.CSSProperties } = {
  colHead: {
    fontSize: 11, fontWeight: 900, letterSpacing: "0.14em",
    color: C.primary, marginBottom: 4, fontFamily: C.display,
    textShadow: TEXT_3D_LIGHT,
  },
};